import React, { Component } from 'react';
import { observable } from 'mobx/lib/mobx';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import rValueLookup from './rvaluelookup';
import NumberField from '../numberfield/numberfield.component';
import { observer } from 'mobx-react/index';

import './rvaluefield.scss';

@observer
export default class RValueField extends Component {
    static defaultProps = {
        field: null,
        label: '',
        surface: null,
        glazing: null,
        standard: null,
        disabled: false,
        readOnly: false,
        errors: null,
        era: null,
        zone: null,
        onChange: null,
        onBlur: null,
        hint: 'This R-value is only an estimate — you can edit it if you know the exact value. If you know the R-value is lower you must enter that. Even if there is no insulation a wall, ceiling or floor will still have an R-value.',
        showHint: true,
        default: null,
        warning: false,
        parentWarning: false,
        triggerParentWarning: false
    };

    static propTypes = {
        field: PropTypes.object.isRequired,
        label: PropTypes.string,
        showHint: PropTypes.bool,
        element: PropTypes.oneOf([
            'ceiling',
            'floor',
            'wall',
            'glazing',
            'skylight'
        ]).isRequired,
        era: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        zone: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
        surface: PropTypes.oneOf(['internal', 'external', 'slab', null]),
        glazing: PropTypes.oneOf(['single', 'double', null]),
        standard: PropTypes.oneOf(['standard', 'nonstandard', 'none', null]),
        disabled: PropTypes.bool,
        readOnly: PropTypes.bool,
        errors: PropTypes.string,
        onChange: PropTypes.func,
        onBlur: PropTypes.func,
        warning: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
        parentWarning: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
        triggerParentWarning: PropTypes.oneOfType([PropTypes.func, PropTypes.bool])
    };

    @observable edited = false;
    @observable editing = false;
    @observable displayValue = '';
    @observable numberField = null;
    @observable defaultRValue = null;
    @observable era = null;
    @observable zone = null;
    @observable warning = false;
    @observable parentWarning = false;

    /**
     * if provided with a value, use this for
     * the displayValue. If not provided with
     * a value, get it from lookupRValue;
     */
    constructor(props) {
        super(props);

        if (props.rValueEra) {
            this.era = props.rValueEra;
        } else {
            this.era = props.era;
        }

        this.zone = props.zone;
        this.numberField = React.createRef();
        this.defaultRValue = RValueField.lookupRValue(props);
        this.warningFn = (typeof props.warning === 'function')
            ? props.warning
            : () => { return false; };
        this.parentWarningFn = (typeof props.parentWarning === 'function')
            ? props.parentWarning
            : () => { return false; };

        if (props.value) {
            this.edited = true;
            this.displayValue = props.value;
        } else {
            const value = this.defaultRValue,
                { onChange } = props;

            this.displayValue = value;
            onChange(value);
        }

        this.warning =
            this.warningFn(this.displayValue < this.defaultRValue);
        this.parentWarning =
            this.parentWarningFn(this.displayValue < this.defaultRValue);

        if (this.props.triggerParentWarning) {
            this.props.triggerParentWarning(this.parentWarning);
        }
    }

    componentDidUpdate(prevProps) {
        const { era, zone } = this.props;
        if (this.props.rValueEra) {
            this.era = this.props.rValueEra;
        } else {
            this.era = this.props.era;
        }

        this.zone = zone;
        this.defaultRValue = RValueField.lookupRValue(this.props);

        if (
            (prevProps.era !== era || prevProps.zone !== zone)
        ) {
            const value = this.defaultRValue,
                { onChange } = this.props;

            this.displayValue = parseFloat(value, 10);
            onChange(value);
        }

        this.warning =
            this.warningFn(parseFloat(this.displayValue, 10) < this.defaultRValue);
        this.parentWarning =
            this.parentWarningFn(parseFloat(this.displayValue, 10) < this.defaultRValue);

        // fire the callback that is passed as a prop
        if (this.props.triggerParentWarning) {
            this.props.triggerParentWarning(this.parentWarning);
        }
    }

    /**
     * On blur of the rvalue field, if left blank,
     * fetch the default rvalue for the required
     * params for this element
     */
    onBlur(e) {
        let { value } = e.target;

        this.editing = false;

        if (!value || value === '') {
            value = RValueField.lookupRValue(this.props);

            this.edited = false;
            this.displayValue = value;

            // need to call props onChange for form
            const { onChange } = this.props;
            onChange(value);
        }

        const { onBlur } = this.props;
        onBlur(value);
    }

    /**
     * Called from constructor hence we pass in props
     */
    static lookupRValue(props) {
        const { zone, element, surface, standard, glazing } = props,
            era = props.rValueEra ? props.rValueEra : props.era;

        // if we don't have an era or zone, return
        if (!era || !zone) {
            return 0;
        }

        let value = rValueLookup.rValueForElement({
            era, // e.g. "Before 2008"
            zone, // 1, 2 or 3
            element, // type e.g. floor, wall, ceiling, glaxing or skylight
            surface, // internal vs external for walls / floor / ceiling
            standard, // true false for floor / ceiling
            glazing // single vs double for glazing / skylight
        });

        if (!value) {
            value = 0; // TODO use a fallback. what is it?
        }

        return value;
    };

    toggleEditable(e, id) {
        this.editing = !this.editing;

        const input = document.getElementById(id),
            ios = document.documentElement.classList.contains('ios');

        // ios won't automatically open the keyboard
        // so don't auto focus, as this:
        // a. causes confusion &
        // b. interrupts editing without a timeout set
        if (!ios) {
            input.focus();
        }
    };

    /**
     * On change of the rvalue field, set the
     * displayValue to whatever the user types
     */
    handleChange(e) {
        const { value } = e.target,
            { onChange } = this.props;

        this.edited = true;
        this.displayValue = value;

        // need to call props onChange from form
        onChange(value);
    }

    getEditActionName() {
        const { element, surface, standard, glazing } = this.props,
            parts = [
                element, surface, standard, glazing, 'edit-rvalue'
            ].filter(Boolean),
            action = parts.join('-');

        return action;
    }

    render() {
        const {
                field,
                disabled,
                readOnly,
                errors,
                hint,
                showHint,
                warning,
                parentWarning,
                triggerParentWarning,
                retrofitted2009,
                standards2009Compliant,
                era,
                rValueEra,
                zone,
                ...inputProps
            } = this.props,
            { editing, edited, displayValue } = this,
            fieldClasses = classNames('ht-field ht-rvaluefield', {
                'ht-rvaluefield--editing': editing,
                'ht-rvaluefield--edited': edited
            }),
            hintText = showHint && hint
                ? hint
                : false;

        return (
            <div className={fieldClasses}>
                <NumberField
                    ref={this.numberField}
                    errors={errors}
                    {...inputProps}
                    value={displayValue}
                    readOnly={!editing || readOnly}
                    onChange={e => this.handleChange(e)}
                    onBlur={e => this.onBlur(e)}
                    min={0}
                    max={32}
                    hint={hintText}
                    warning={this.warning}
                >
                    {!disabled && (
                        <button
                            id={`${inputProps.name}-rvalue-edit`}
                            type='button'
                            className='ht-rvaluefield-editbutton'
                            onClick={(e) => this.toggleEditable(e, field.id)}
                            data-action={this.getEditActionName()}
                            data-action-era={this.props.era}
                            data-action-zone={this.props.zone}
                            data-action-element={this.props.element}
                            data-action-surface={this.props.surface}
                            data-action-glazing={this.props.glazing}
                        >
                            Edit
                        </button>
                    )}
                </NumberField>
            </div>
        );
    }
}
